import { supabase } from '../lib/supabase';
import { fetchAllRows } from '../lib/fetchAllRows';

export interface DashboardStats {
  totalUsers: number;
  newUsersThisMonth: number;
  newUsersLastMonth: number;
  totalSongs: number;
  activeTournaments: number;
  totalPerformances: number;
  totalRecordings: number;
  averageScore: number;
  activeSubscriptions: number;
  cancelledSubscriptions: number;
  subscriptionsByType: { plan_type: string; count: number }[];
  revenueFromPurchases: number;
  revenueFromSubscriptions: number;
  userSignupsByMonth: { month: string; count: number }[];
  recordingsByMonth: { month: string; count: number }[];
  transactionsByType: { type: string; total: number; count: number }[];
}

export interface Song {
  id: string;
  title: string;
  artist: string;
  genre: string;
  release_year: number;
  difficulty: string;
  duration: number;
  audio_url: string;
  cover_url: string;
  lead_vocal_url: string | null;
  lyrics_url: string | null;
  midi_url: string | null;
  full_mix_url: string | null;
  distribution_eligible: boolean;
  is_deleted?: boolean;
  deleted_at?: string | null;
  deleted_by?: string | null;
  is_audition?: boolean;
  backing_track_lufs?: number | null;
}

export class AdminService {
  async updateUserRole(userId: string, newRole: 'user' | 'admin'): Promise<void> {
    try {
      const { error } = await supabase
        .rpc('update_user_role', {
          target_user_id: userId,
          new_role: newRole
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  }

  async getDashboardStats(): Promise<DashboardStats> {
    try {
      const now = new Date();
      const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();
      const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1).toISOString();

      const [
        { count: userCount },
        { count: newUsersThisMonth },
        { count: newUsersLastMonth },
        { count: songCount },
        { count: tournamentCount },
        { count: performanceCount },
        { count: recordingCount },
        performanceScores,
        activeSubs,
        cancelledSubs,
        allSubs,
        purchaseTx,
        subscriptionTx,
        allProfiles,
        allRecordings,
        allTransactions,
      ] = await Promise.all([
        supabase.from('profiles').select('*', { count: 'exact', head: true }),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).gte('created_at', startOfThisMonth),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).gte('created_at', startOfLastMonth).lt('created_at', startOfThisMonth),
        supabase.from('songs').select('*', { count: 'exact', head: true }).eq('is_deleted', false),
        supabase.from('tournaments').select('*', { count: 'exact', head: true }).eq('status', 'active'),
        supabase.from('performances').select('*', { count: 'exact', head: true }),
        supabase.from('recordings').select('*', { count: 'exact', head: true }),
        fetchAllRows<{ score: number }>(() => supabase.from('performances').select('score')),
        fetchAllRows<{ id: string; plan_type: string }>(() => supabase.from('subscriptions').select('id, plan_type').eq('status', 'active')),
        fetchAllRows<{ id: string }>(() => supabase.from('subscriptions').select('id').eq('status', 'cancelled')),
        fetchAllRows<{ plan_type: string; status: string }>(() => supabase.from('subscriptions').select('plan_type, status')),
        fetchAllRows<{ amount: number }>(() => supabase.from('coin_transactions').select('amount').eq('transaction_type', 'purchase').gte('created_at', startOfThisMonth)),
        fetchAllRows<{ amount: number }>(() => supabase.from('coin_transactions').select('amount').eq('transaction_type', 'subscription').gte('created_at', startOfThisMonth)),
        fetchAllRows<{ created_at: string }>(() => supabase.from('profiles').select('created_at').gte('created_at', sixMonthsAgo).order('created_at', { ascending: true })),
        fetchAllRows<{ created_at: string }>(() => supabase.from('recordings').select('created_at').gte('created_at', sixMonthsAgo).order('created_at', { ascending: true })),
        fetchAllRows<{ transaction_type: string; amount: number }>(() => supabase.from('coin_transactions').select('transaction_type, amount')),
      ]);

      const avgScore = (performanceScores || []).reduce((acc, curr) => acc + (curr.score || 0), 0) / (performanceCount || 1);

      const subsByType: Record<string, number> = {};
      (activeSubs || []).forEach(s => {
        subsByType[s.plan_type] = (subsByType[s.plan_type] || 0) + 1;
      });
      const subscriptionsByType = Object.entries(subsByType).map(([plan_type, count]) => ({ plan_type, count }));

      const monthLabels: string[] = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        monthLabels.push(d.toLocaleString('default', { month: 'short', year: '2-digit' }));
      }

      const bucketByMonth = (items: { created_at: string }[]) => {
        const counts: Record<string, number> = {};
        monthLabels.forEach(m => { counts[m] = 0; });
        (items || []).forEach(item => {
          const d = new Date(item.created_at);
          const key = d.toLocaleString('default', { month: 'short', year: '2-digit' });
          if (counts[key] !== undefined) counts[key]++;
        });
        return monthLabels.map(month => ({ month, count: counts[month] }));
      };

      const userSignupsByMonth = bucketByMonth(allProfiles || []);
      const recordingsByMonth = bucketByMonth(allRecordings || []);

      const txGrouped: Record<string, { total: number; count: number }> = {};
      (allTransactions || []).forEach(tx => {
        const type = tx.transaction_type;
        if (!txGrouped[type]) txGrouped[type] = { total: 0, count: 0 };
        txGrouped[type].total += Math.abs(parseFloat(tx.amount?.toString() || '0'));
        txGrouped[type].count += 1;
      });
      const transactionsByType = Object.entries(txGrouped).map(([type, v]) => ({ type, total: v.total, count: v.count }));

      return {
        totalUsers: userCount || 0,
        newUsersThisMonth: newUsersThisMonth || 0,
        newUsersLastMonth: newUsersLastMonth || 0,
        totalSongs: songCount || 0,
        activeTournaments: tournamentCount || 0,
        totalPerformances: performanceCount || 0,
        totalRecordings: recordingCount || 0,
        averageScore: Math.round(avgScore || 0),
        activeSubscriptions: (activeSubs || []).length,
        cancelledSubscriptions: (cancelledSubs || []).length,
        subscriptionsByType,
        revenueFromPurchases: (purchaseTx || []).reduce((sum, t) => sum + parseFloat(t.amount?.toString() || '0'), 0),
        revenueFromSubscriptions: (subscriptionTx || []).reduce((sum, t) => sum + parseFloat(t.amount?.toString() || '0'), 0),
        userSignupsByMonth,
        recordingsByMonth,
        transactionsByType,
      };
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      throw error;
    }
  }

  async getAllSongs(includeDeleted: boolean = false): Promise<Song[]> {
    try {
      console.log('AdminService: Fetching songs from database', { includeDeleted });
      const data = await fetchAllRows<any>(() => {
        let query = supabase.from('songs').select('*');
        if (!includeDeleted) query = query.eq('is_deleted', false);
        return query.order('title');
      });
      console.log('AdminService: Raw song data:', data);

      // Process the URLs to ensure they're public URLs
      const processedSongs = data?.map(song => ({
        ...song,
        cover_url: song.cover_url ? (() => {
          // Extract just the filename from the path
          const cleanPath = song.cover_url.split('/').pop() || '';
          console.log('AdminService: Processing cover URL:', {
            original: song.cover_url,
            cleaned: cleanPath
          });
          const { data: { publicUrl } } = supabase.storage
            .from('song_covers')
            .getPublicUrl(cleanPath);
          console.log('AdminService: Generated public URL:', publicUrl);
          return publicUrl;
        })() : null,
        audio_url: song.audio_url ? (() => {
          const cleanPath = song.audio_url.split('/').pop() || '';
          return supabase.storage
            .from('song_audio')
            .getPublicUrl(cleanPath).data.publicUrl;
        })() : null,
        lyrics_url: song.lyrics_url ? (() => {
          const cleanPath = song.lyrics_url.split('/').pop() || '';
          return supabase.storage
            .from('song_lyrics')
            .getPublicUrl(cleanPath).data.publicUrl;
        })() : null
      }));

      console.log('AdminService: Processed song data:', processedSongs);
      return processedSongs || [];
    } catch (error) {
      console.error('Error loading songs:', error);
      throw error;
    }
  }

  async createSong(songData: Partial<Song>): Promise<Song> {
    try {
      const { data, error } = await supabase
        .from('songs')
        .insert([songData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating song:', error);
      throw error;
    }
  }

  async updateSong(id: string, songData: Partial<Song>): Promise<Song> {
    try {
      const { data, error } = await supabase
        .from('songs')
        .update(songData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating song:', error);
      throw error;
    }
  }

  async deleteSong(id: string): Promise<void> {
    try {
      console.log('AdminService: Attempting to soft delete song with ID:', id);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      // Soft delete by setting is_deleted flag
      const { error } = await supabase
        .from('songs')
        .update({
          is_deleted: true,
          deleted_at: new Date().toISOString(),
          deleted_by: user?.id || null
        })
        .eq('id', id);

      if (error) {
        console.error('AdminService: Supabase error soft deleting song:', error);
        throw error;
      }

      console.log('AdminService: Successfully soft deleted song from database');
    } catch (error) {
      console.error('AdminService: Error in deleteSong method:', error);
      throw error;
    }
  }

  async restoreSong(id: string): Promise<void> {
    try {
      console.log('AdminService: Attempting to restore song with ID:', id);

      // Restore by clearing is_deleted flag
      const { error } = await supabase
        .from('songs')
        .update({
          is_deleted: false,
          deleted_at: null,
          deleted_by: null
        })
        .eq('id', id);

      if (error) {
        console.error('AdminService: Supabase error restoring song:', error);
        throw error;
      }

      console.log('AdminService: Successfully restored song');
    } catch (error) {
      console.error('AdminService: Error in restoreSong method:', error);
      throw error;
    }
  }

  async createUser(userData: {
    email: string;
    username: string;
    full_name: string;
    role?: 'user' | 'admin';
  }): Promise<any> {
    try {
      // Validate required fields
      if (!userData.email?.trim() || !userData.username?.trim() || !userData.full_name?.trim()) {
        throw new Error('Email, username, and full name are required');
      }

      // Split full name into first and last name
      const nameParts = userData.full_name.trim().split(/\s+/);
      if (nameParts.length < 2) {
        throw new Error('Full name must include both first and last name');
      }

      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ');

      // Generate a secure random password
      const password = Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

      console.log('AdminService: Creating user with edge function. Details:', {
        email: userData.email,
        username: userData.username,
        firstName,
        lastName,
        role: userData.role || 'user'
      });

      // Prepare the request body
      const requestBody = {
        email: userData.email.trim(),
        password,
        firstName,
        lastName,
        username: userData.username.trim(),
        roles: [userData.role || 'user']
      };

      // First check if the email already exists
      try {
        const { data: emailExists, error: emailCheckError } = await supabase.rpc('check_email_exists', {
          p_email: userData.email.trim()
        });
        
        if (emailCheckError) {
          console.error('AdminService: Error checking email existence:', emailCheckError);
        } else if (emailExists) {
          throw new Error('The email is already in use. Please try a different one.');
        }
      } catch (emailCheckError) {
        console.error('AdminService: Error in email check:', emailCheckError);
        // Continue with the request, the edge function will handle duplicates
      }

      // Check if username exists
      try {
        const { data: existingUser, error: usernameCheckError } = await supabase
          .from('profiles')
          .select('username')
          .eq('username', userData.username.trim())
          .maybeSingle();
          
        if (usernameCheckError) {
          console.error('AdminService: Error checking username existence:', usernameCheckError);
        } else if (existingUser) {
          throw new Error('This username is already taken. Please choose a different one.');
        }
      } catch (usernameCheckError) {
        if (usernameCheckError instanceof Error && 
            !usernameCheckError.message.includes('relation "profiles" does not exist')) {
          throw usernameCheckError;
        }
        console.error('AdminService: Error in username check:', usernameCheckError);
        // Continue with the request, the edge function will handle duplicates
      }

      const response = await fetch(`${supabase.supabaseUrl}/functions/v1/create-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabase.supabaseKey}`
        },
        body: JSON.stringify(requestBody)
      });

      const responseData = await response.json();

      if (!response.ok) {
        console.error('AdminService: Error response from create-user function:', {
          status: response.status,
          statusText: response.statusText,
          error: responseData
        });

        // Handle specific error cases
        if (responseData.error?.includes('already exists')) {
          throw new Error('The email or username may already be in use. Please try a different one.');
        }

        throw new Error(responseData.error || `Failed to create user: ${response.statusText}`);
      }

      if (!responseData.user) {
        console.error('AdminService: No user data in response:', responseData);
        throw new Error('User creation failed: No user data returned');
      }

      console.log('AdminService: User created successfully:', responseData);
      return responseData;
    } catch (error) {
      console.error('AdminService: Error creating user:', error);
      
      // Check if this appears to be a database constraint error
      if (error instanceof Error && 
          (error.message.includes('duplicate key') || 
           error.message.includes('already exists') || 
           error.message.includes('already in use'))) {
        console.error('AdminService: This appears to be a database constraint error. Check for duplicate email or username:', 
          userData.email, '\n', userData.username);
      }
      
      throw error;
    }
  }

  async updateUser(userId: string, userData: Partial<User>): Promise<void> {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          username: userData.username,
          full_name: userData.full_name,
          role: userData.role
        })
        .eq('id', userId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  async deleteUser(userId: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('delete_user', {
        target_user_id: userId
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  async deleteOwnAccount(): Promise<void> {
    const { error } = await supabase.rpc('delete_own_account');
    if (error) throw error;
  }

  async uploadFile(file: File, path: string): Promise<string> {
    try {
      console.log('AdminService: Starting file upload:', { path, type: file.type });
      
      // Sanitize filename
      const sanitizeFilename = (name: string): string => {
        // Remove any path traversal characters and spaces
        const sanitized = name
          .replace(/[^a-zA-Z0-9.-]/g, '_') // Replace any non-alphanumeric chars (except . and -) with _
          .replace(/\s+/g, '_') // Replace spaces with underscores
          .toLowerCase(); // Convert to lowercase
        
        // Ensure the extension is preserved
        const ext = name.split('.').pop() || '';
        return `${Date.now()}_${sanitized}`;
      };
      
      // Determine which bucket to use based on file type
      const bucket = path.startsWith('audio/') ? 'song_audio' :
                    path.startsWith('lyrics/') ? 'song_lyrics' :
                    path.startsWith('full-mix/') ? 'song_audio' : // Practice tracks go to song_audio
                    path.startsWith('midi/') ? 'song_audio' : // MIDI files go to song_audio
                    path.startsWith('vocals/') ? 'song_audio' : // Vocals-only tracks go to song_audio
                    'song_covers';
      
      // Get original filename and sanitize it
      const originalFilename = path.split('/').pop() || '';
      const filename = sanitizeFilename(originalFilename);
      
      console.log('AdminService: Uploading to bucket:', { bucket, filename });
      
      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(filename, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) {
        console.error('AdminService: Upload error:', error);
        throw error;
      }

      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(filename);
      
      console.log('AdminService: Upload successful:', { 
        originalFilename,
        sanitizedFilename: filename,
        publicUrl 
      });

      return publicUrl;
    } catch (error) {
      console.error(`Error uploading ${path}:`, error);
      throw error;
    }
  }

  async measureTrackLoudness(songId: string): Promise<{ backing_track_lufs: number }> {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
    const res = await fetch(`${supabaseUrl}/functions/v1/measure-track-loudness`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${anonKey}`,
        'apikey': anonKey,
      },
      body: JSON.stringify({ song_id: songId }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to measure loudness');
    return data;
  }
}

// Add User interface to support updateUser method
interface User {
  id: string;
  email: string;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
  role: 'user' | 'admin';
  created_at: string;
}